import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAddAuditCaseDocument } from '@/queries/add-audit-case-document';
import { useDeleteAuditCaseDocument } from '@/queries/delete-audit-case-document';
import { useNotificationStore } from '@/store/notificationStore';
import type { DocumentDefinition, UploadedDocument } from '@/types/documentUpload';

import { DocumentsTable } from './documents-table';
import { isFileSizeValid, resolveLanguageOptions } from './helpers';
import { DocumentDeleteModal, DocumentUploadModal } from './modals';
import type { DocumentUploadModalResult } from './modals/document-upload-modal';

interface DocumentsTypeWrapperProps {
  documentDefinition: DocumentDefinition;
  documents: UploadedDocument[];
  auditCaseUuid: string;
  moduleId: string;
  translations: Record<string, string>;
}

interface UploadConfigs {
  allowedFileTypes: string[];
  displayName?: string;
  language?: string;
  onSuccess?: () => void;
}

export const DocumentTypeWrapper = ({
  documentDefinition,
  documents,
  auditCaseUuid,
  moduleId,
  translations,
}: DocumentsTypeWrapperProps) => {
  const { t } = useTranslation();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteDocOptions, setDeleteDocOptions] = useState<UploadedDocument | null>(null);

  const notify = useNotificationStore((state) => state.notify);
  const { mutate: deleteDocument } = useDeleteAuditCaseDocument();
  const { mutate: addDocument, isPending: isUploading } = useAddAuditCaseDocument();

  const languageOptions = resolveLanguageOptions(documentDefinition, t);

  const openFilePicker = (configs: UploadConfigs) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = configs.allowedFileTypes.map((ext) => `.${ext}`).join(',');
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      if (!isFileSizeValid(file.size, documentDefinition.maxFileSize)) {
        notify({
          message: translations.fileTooLarge.replace(
            '{{maxFileSize}}',
            String(documentDefinition.maxFileSize),
          ),
          type: 'error',
        });
        return;
      }
      addDocument(
        {
          auditCaseUuid,
          moduleId,
          docDefinitionId: documentDefinition.id,
          file,
          displayName: configs.displayName,
          language: configs.language,
        },
        { onSuccess: configs.onSuccess },
      );
    };
    input.click();
  };

  const handleUploadClick = () => {
    if (documentDefinition.isNameRequired || documentDefinition.languageSensitive) {
      setShowUploadModal(true);
    } else {
      openFilePicker({
        allowedFileTypes: documentDefinition.allowedFileTypes,
      });
    }
  };

  const handleModalContinue = (result: DocumentUploadModalResult) => {
    openFilePicker({
      allowedFileTypes: documentDefinition.allowedFileTypes,
      displayName: result.displayName,
      language: result.language,
      onSuccess: () => setShowUploadModal(false),
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteDocOptions) return;
    deleteDocument(
      { auditCaseUuid, documentUuid: deleteDocOptions.uuid },
      {
        onSettled: () => setDeleteDocOptions(null),
      },
    );
  };

  return (
    <>
      <DocumentsTable
        id={documentDefinition.id}
        typeName={documentDefinition.type.name}
        isRequired={documentDefinition.isRequired}
        isUploadable={documentDefinition.isUploadable}
        isUploading={isUploading}
        documents={documents}
        translations={translations}
        auditCaseUuid={auditCaseUuid}
        onUploadClick={handleUploadClick}
        onDeleteClick={setDeleteDocOptions}
      />

      {showUploadModal && (
        <DocumentUploadModal
          typeName={documentDefinition.type.name}
          isNameRequired={documentDefinition.isNameRequired}
          isLanguageSensitive={documentDefinition.languageSensitive}
          languageOptions={languageOptions}
          isUploading={isUploading}
          translations={translations}
          onContinue={handleModalContinue}
          onClose={() => setShowUploadModal(false)}
        />
      )}

      {deleteDocOptions && (
        <DocumentDeleteModal
          typeName={documentDefinition.type.name}
          fileName={deleteDocOptions.displayName ?? deleteDocOptions.fileName}
          translations={translations}
          onDelete={handleDeleteConfirm}
          onClose={() => setDeleteDocOptions(null)}
        />
      )}
    </>
  );
};
